/**
 * PermissionModal Component
 * Displays user permissions, custom permissions, and permission holders for master data
 */

import { useState } from 'react';

interface PermissionHolder {
  userId: number;
  userName: string;
  email?: string;
  roleName?: string;
  mobile?: string;
}

interface Permissions {
  c_in?: boolean;
  r_in?: boolean;
  u_in?: boolean;
  d_in?: boolean;
  customPermissions?: Record<string, boolean>;
  holders?: PermissionHolder[];
}

interface PermissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  permissions: Permissions;
  entityName: string;
}

export default function PermissionModal({ isOpen, onClose, permissions, entityName }: PermissionModalProps) {
  const [showHolders, setShowHolders] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    setShowHolders(false);
    onClose();
  };

  const hasCustomPermissions = permissions?.customPermissions && Object.keys(permissions.customPermissions).length > 0;
  const hasHolders = permissions?.holders && permissions.holders.length > 0;

  const formatPermissionLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <i className="ri-shield-user-line text-xl text-white"></i>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Permissions</h3>
                <p className="text-indigo-100 text-sm">{entityName}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white p-1"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {!showHolders ? (
            <>
              {/* CRUD Permissions */}
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Your Access Level</div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <i className="ri-add-line text-lg text-gray-600 dark:text-gray-300"></i>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Create</span>
                  </div>
                  {permissions?.c_in ? (
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                  ) : (
                    <i className="ri-close-circle-fill text-xl text-red-400"></i>
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <i className="ri-eye-line text-lg text-gray-600 dark:text-gray-300"></i>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Read</span>
                  </div>
                  {permissions?.r_in ? (
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                  ) : (
                    <i className="ri-close-circle-fill text-xl text-red-400"></i>
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <i className="ri-edit-line text-lg text-gray-600 dark:text-gray-300"></i>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Update</span>
                  </div>
                  {permissions?.u_in ? (
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                  ) : (
                    <i className="ri-close-circle-fill text-xl text-red-400"></i>
                  )}
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <i className="ri-delete-bin-line text-lg text-gray-600 dark:text-gray-300"></i>
                    <span className="font-medium text-gray-700 dark:text-gray-200">Delete</span>
                  </div>
                  {permissions?.d_in ? (
                    <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                  ) : (
                    <i className="ri-close-circle-fill text-xl text-red-400"></i>
                  )}
                </div>
              </div>

              {/* Custom Permissions */}
              {hasCustomPermissions && (
                <>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 mt-5">Custom Permissions</div>
                  <div className="space-y-2 mb-4">
                    {Object.entries(permissions.customPermissions!).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex items-center gap-3">
                          <i className="ri-checkbox-blank-circle-line text-lg text-gray-600 dark:text-gray-300"></i>
                          <span className="font-medium text-gray-700 dark:text-gray-200">{formatPermissionLabel(key)}</span>
                        </div>
                        {value ? (
                          <i className="ri-checkbox-circle-fill text-xl text-green-500"></i>
                        ) : (
                          <i className="ri-close-circle-fill text-xl text-red-400"></i>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* View Holders Button */}
              <button
                onClick={() => setShowHolders(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors font-medium text-sm"
              >
                <i className="ri-group-line"></i>
                View Permission Holders
              </button>
            </>
          ) : (
            <>
              {/* Back Button */}
              <button
                onClick={() => setShowHolders(false)}
                className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 mb-3"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Permissions
              </button>

              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Permission Holders</div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Contact these people to request access</p>

              {/* Admin Users */}
              {hasHolders ? (
                <div className="mb-4">
                  <div className="space-y-2">
                    {permissions.holders!.map((user: PermissionHolder, idx: number) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="w-9 h-9 bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300 rounded-full flex items-center justify-center text-sm">
                          <i className="ri-admin-line"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-700 dark:text-gray-200 text-sm">{user.userName}</div>
                          {user.roleName && <div className="text-xs text-gray-500 dark:text-gray-400">{user.roleName}</div>}
                          {user.mobile && <div className="text-xs text-purple-600 dark:text-purple-400">{user.mobile}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <i className="ri-user-unfollow-line text-3xl text-gray-300 dark:text-gray-600 mb-2 block"></i>
                  <p className="text-sm">No administrator information available</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
